import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ColumnConfig, DropdownMenuItem } from '@/components/ui';
import { AccountsTable as SharedAccountsTable } from '@/pages/console/accounts/shared/AccountsTable';
import { IdentityCell, StatusChip, type CellTone } from '@/pages/console/accounts/shared/cells';
import type { ScopedAccountRow } from '@/api/orgAdmin';
import type { ScopedAccountStatus } from './types';
import type { ScopedAccountsVM } from './useScopedAccounts';
import type { ScopedAccountWritesVM } from './useScopedAccountWrites';

/** Priority order matters: a revoked grant is "Đã ngưng" whatever its sync state says. */
function statusOf(row: ScopedAccountRow): ScopedAccountStatus {
    if (!row.is_active) return 'revoked';
    if (row.sync_status === 'error') return 'sync-error';
    if (row.sync_status === 'pending') return 'pending';
    return 'ok';
}

function statusToneOf(status: ScopedAccountStatus): CellTone {
    return status === 'ok' ? 'ok' : status === 'pending' ? 'warn' : 'off';
}

function statusTextOf(t: TFunction, status: ScopedAccountStatus): string {
    switch (status) {
        case 'revoked':
            return t('console.hierarchy.scopedAccounts.table.statusRevoked');
        case 'sync-error':
            return t('console.hierarchy.scopedAccounts.table.statusSyncError');
        case 'pending':
            return t('console.hierarchy.scopedAccounts.table.statusPending');
        default:
            return t('console.hierarchy.scopedAccounts.table.statusOk');
    }
}

function rowActionItems(
    row: ScopedAccountRow,
    vm: ScopedAccountsVM,
    writes: ScopedAccountWritesVM,
    onTransfer: (row: ScopedAccountRow) => void,
    t: TFunction
): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [];
    if (vm.canReset) {
        items.push({
            key: 'reset',
            label: t('console.hierarchy.scopedAccounts.table.actionResetPassword'),
            onSelect: () => writes.setResetTarget(row),
        });
    }
    // EAA-1820 (frame 85): "Chuyển đơn vị" moves the row's Assignment to another
    // đơn vị in place — same `canManage` gate as the other privileged row
    // actions on this table (`useScopedAccounts.canManage`), and only offered on
    // an active row for the same reason "Thu hồi" is: a revoked row has nothing
    // left to move.
    if (vm.canManage && row.is_active) {
        items.push({
            key: 'transfer',
            label: t('console.hierarchy.scopedAccounts.table.actionTransfer'),
            onSelect: () => onTransfer(row),
        });
    }
    if (vm.canManage && row.is_active) {
        items.push({
            key: 'revoke',
            label: t('console.hierarchy.scopedAccounts.table.actionRevoke'),
            danger: true,
            onSelect: () => writes.setRevokeTarget(row),
        });
    }
    return items;
}

/**
 * EAA-1820 (screen unification pass): the org accounts columns, now rendered
 * through the SAME `shared/AccountsTable` the realm screen uses (was a
 * hand-rolled `<table>` with its own checkbox/sort-header/status-dot markup —
 * see this file's git history / EAA-1839 for that version). Column CONTENT
 * stays org-specific:
 *
 *  - TÀI KHOẢN is email + `user_sub`, not name + username + email.
 *    `AssignmentSerializer` exposes `user_sub`/`user_email` only — no Keycloak
 *    join on this endpoint — so inventing a display name would be a fabrication
 *    this ticket forbids.
 *  - LEVEL & VAI TRÒ is the org-only column (vs. the realm's "Vai trò ứng dụng"):
 *    `level_name` plus the org's own resolved app-role keys, when the row
 *    carries them; "Chọn Level" surfaces the exact "Chưa có Level" gap the
 *    design calls out.
 *  - ĐĂNG NHẬP renders "Không có dữ liệu": Keycloak's user list API exposes no
 *    last-login instant, and this endpoint has no Keycloak join at all to ask.
 *
 * Only `user_sub` and `sync_status` are sortable — `ALLOWED_ORDER_FIELDS`.
 */
export function ScopedAccountsTable({
    vm,
    writes,
    onTransfer,
}: {
    vm: ScopedAccountsVM;
    writes: ScopedAccountWritesVM;
    /** EAA-1820 (frame 85): opens `RowTransferDialog` for this row. */
    onTransfer: (row: ScopedAccountRow) => void;
}) {
    const { t } = useTranslation();

    const columns: ColumnConfig<ScopedAccountRow>[] = [
        {
            key: 'user_sub',
            title: t('console.hierarchy.scopedAccounts.table.columnAccount'),
            sorter: true,
            render: (_value, row) => (
                <IdentityCell
                    primary={row.user_email || t('console.hierarchy.scopedAccounts.table.noEmail')}
                    secondary={row.user_sub}
                />
            ),
        },
        {
            key: 'org',
            title: t('console.hierarchy.scopedAccounts.table.columnOrg'),
            render: (_value, row) =>
                vm.orgNameById.get(row.organization_id) ?? `${row.organization_id.slice(0, 8)}…`,
        },
        {
            key: 'level',
            title: t('console.hierarchy.scopedAccounts.table.columnLevel'),
            render: (_value, row) =>
                row.level_name ? (
                    row.level_name
                ) : (
                    <span className="sa-warn-text">{t('console.hierarchy.scopedAccounts.table.noLevel')}</span>
                ),
        },
        {
            key: 'sync_status',
            title: t('console.hierarchy.scopedAccounts.table.columnStatus'),
            sorter: true,
            render: (_value, row) => {
                const status = statusOf(row);
                return <StatusChip tone={statusToneOf(status)} label={statusTextOf(t, status)} />;
            },
        },
        {
            key: 'last_login',
            title: t('console.hierarchy.scopedAccounts.table.columnLastLogin'),
            render: () => (
                <span
                    className="portal-accounts-unavailable-cell"
                    title={t('console.hierarchy.scopedAccounts.table.lastLoginUnavailable')}
                >
                    {t('console.hierarchy.scopedAccounts.table.lastLoginEmpty')}
                </span>
            ),
        },
    ];

    return (
        <SharedAccountsTable<ScopedAccountRow>
            rows={vm.rows}
            columns={columns}
            rowKey="id"
            selectedKeys={[...vm.selected.keys()]}
            onSelectedKeysChange={(keys) => {
                // EAA-1820: the shared table's selection callback is key-only
                // (`DataTable`'s own `rowSelection.onChange` shape), but this
                // screen's selection model is a Map<id, ScopedAccountRow> — bulk
                // Level-assign and "select all matching" both need the whole row,
                // not just its id (see `useScopedAccounts.selected`). `toggleRow`
                // already flips exactly one row in/out of that map, so reconcile
                // by toggling every row on THIS page whose ticked state changed —
                // off-page rows already selected via "select all matching" are
                // untouched since they are not in `vm.rows`.
                const nextIds = new Set(keys);
                for (const row of vm.rows) {
                    const wasSelected = vm.selected.has(row.id);
                    const nowSelected = nextIds.has(row.id);
                    if (wasSelected !== nowSelected) vm.toggleRow(row);
                }
            }}
            rowActionsAriaLabel={(row) =>
                t('console.hierarchy.scopedAccounts.table.rowActionsAriaLabel', {
                    identity: row.user_email || row.user_sub,
                })
            }
            rowActions={(row) => rowActionItems(row, vm, writes, onTransfer, t)}
            emptyText={t('console.accounts.table.emptyText')}
        />
    );
}

export default ScopedAccountsTable;
